import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { addNotification } from '../../public/site/notificationStore.js';
import { productSelectionMissingMsg, t } from '../i18n/index.js';
import { getDeviceSelectionProductTypeLabel } from '../utils/deviceListTabsUtilsCoCo.js';
import { getFirstDeviceSelectionCategory } from '../utils/deviceSelectionCategoryUtilsCoCo.js';
import { filterDeviceIdsByProductType } from '../utils/deviceSelectionDeviceUtilsCoCo.js';
import {
  DeviceSelectionFormFieldCoCo,
  getDeviceSelectionFormDefaultValues,
} from '../utils/deviceSelectionFormUtilsCoCo.js';
import type { DeviceSelectionProductTypeCoCo } from '../utils/deviceListTabsUtilsCoCo.js';
import type { DeviceSelectionDevicesByProductTypeCoCo } from '../utils/deviceSelectionDeviceUtilsCoCo.js';
import type { DeviceSelectionFormValuesCoCo } from '../utils/deviceSelectionFormUtilsCoCo.js';
import type { UseFormSetValue } from 'react-hook-form';

const updateProductType = (
  productType: DeviceSelectionProductTypeCoCo | undefined,
  setValue: UseFormSetValue<DeviceSelectionFormValuesCoCo>
) => {
  setValue(DeviceSelectionFormFieldCoCo.CATEGORY, getFirstDeviceSelectionCategory(productType));
  setValue(DeviceSelectionFormFieldCoCo.SELECTED_DEVICE_IDS, []);
};

export const useDeviceSelectionFormCoCo = (
  availableProductTypes: DeviceSelectionProductTypeCoCo[],
  devicesByProductType: DeviceSelectionDevicesByProductTypeCoCo,
  onValidSubmit: (values: DeviceSelectionFormValuesCoCo) => void = () => undefined,
  initialValues?: DeviceSelectionFormValuesCoCo,
  lockedProductType?: DeviceSelectionProductTypeCoCo
) => {
  const firstAvailableProductType = availableProductTypes[0];
  const initialProductType =
    initialValues?.productType && availableProductTypes.includes(initialValues.productType)
      ? initialValues.productType
      : firstAvailableProductType;
  const defaultValues = getDeviceSelectionFormDefaultValues(initialProductType);
  const initialDevices = initialProductType ? devicesByProductType[initialProductType] : [];
  // An edit catalog may not resolve every existing variant group. Keep those IDs unchanged.
  const selectedDeviceIds = lockedProductType
    ? (initialValues?.selectedDeviceIds ?? [])
    : filterDeviceIdsByProductType(
        initialValues?.selectedDeviceIds ?? defaultValues.selectedDeviceIds,
        initialProductType,
        initialDevices
      );
  const methods = useForm<DeviceSelectionFormValuesCoCo>({
    mode: 'onTouched',
    defaultValues: {
      ...defaultValues,
      ...initialValues,
      productType: initialProductType,
      selectedDeviceIds,
    },
  });
  const { clearErrors, getValues, handleSubmit, setError, setValue } = methods;
  const selectedProductType = useWatch({ control: methods.control, name: DeviceSelectionFormFieldCoCo.PRODUCT_TYPE });
  const devices = selectedProductType ? devicesByProductType[selectedProductType] : [];

  // Select the first category and clear all device selections when the product type changes.
  const onProductTypeChange = (productType?: DeviceSelectionProductTypeCoCo) => {
    if (!lockedProductType && productType !== getValues(DeviceSelectionFormFieldCoCo.PRODUCT_TYPE)) {
      updateProductType(productType, setValue);
    }
  };

  // The first available product type is always selected, also when the available product types change
  useEffect(() => {
    const productType = getValues(DeviceSelectionFormFieldCoCo.PRODUCT_TYPE);
    if (!productType || !availableProductTypes.includes(productType)) {
      setValue(DeviceSelectionFormFieldCoCo.PRODUCT_TYPE, firstAvailableProductType);
      updateProductType(firstAvailableProductType, setValue);
    }
  }, [availableProductTypes, firstAvailableProductType, getValues, setValue]);

  const productTypeItems = availableProductTypes.map(productType => ({
    label: getDeviceSelectionProductTypeLabel(productType),
    value: productType,
  }));

  const onSubmit = handleSubmit(values => {
    if (values.selectedDeviceIds.length === 0) {
      setError(DeviceSelectionFormFieldCoCo.SELECTED_DEVICE_IDS, {
        type: 'manual',
        message: t.RZXL(productSelectionMissingMsg),
      });
      addNotification(t.RZXL(productSelectionMissingMsg), 'error');
      return;
    }

    clearErrors(DeviceSelectionFormFieldCoCo.SELECTED_DEVICE_IDS);
    onValidSubmit(values);
  });

  return { devices, methods, onProductTypeChange, onSubmit, productTypeItems };
};
